import React,{useEffect,useRef,useState} from 'react';
import {FiArrowRight,FiCheck,FiFileText,FiLayers,FiUploadCloud,FiX} from 'react-icons/fi';
import {resolveApiBase} from '../lib/transcriptionApi.js';
import {formatTime} from '../lib/editModel.js';
import {readProductionBrief} from '../lib/productionBrief.js';
import {storySourcesFromRows,storyRequestSources,storyDraftDuration,buildPaperFromStory} from '../lib/storyDraft.js';
import {initialDraftTargetSeconds,projectTargetNote} from '../lib/storyTargetNote.js';

const TYPES=[
  ['documentary','Documentary / interview film'],
  ['corporate','Corporate profile'],
  ['testimonial','Customer testimonial'],
  ['case-study','Case study'],
  ['explainer','Explainer / educational'],
  ['social','Short social film']
];
const SECTIONS=[['beginning','Beginning'],['middle','Middle'],['end','End']];
export default function StoryDraft({open,onClose,rows,rootId,paper,getAccessToken,onUse,onLocate}) {
  const [brief,setBrief]=useState('');
  const [briefFile,setBriefFile]=useState('');
  const [briefImporting,setBriefImporting]=useState(false);
  const [briefImportError,setBriefImportError]=useState('');
  const [videoType,setVideoType]=useState('documentary');
  const initialTargetSeconds=initialDraftTargetSeconds(paper.targetRuntimeSeconds);
  const [targetMinutes,setTargetMinutes]=useState(String(Math.floor(initialTargetSeconds/60)));
  const [targetRemainderSeconds,setTargetRemainderSeconds]=useState(String(initialTargetSeconds%60));
  const [chosenIds,setChosenIds]=useState(null);
  const [enabled,setEnabled]=useState(null);
  const [busy,setBusy]=useState(false);
  const [applying,setApplying]=useState(false);
  const [error,setError]=useState('');
  const [draft,setDraft]=useState(null);
  const [draftSources,setDraftSources]=useState([]);
  const [applyMode,setApplyMode]=useState('append');
  const [confirmReplace,setConfirmReplace]=useState(false);
  const active=useRef(null);
  const sources=storySourcesFromRows(rows,rootId);
  const allIds=sources.map(s=>s.id);
  const selectedIds=(chosenIds??allIds).filter(id=>allIds.includes(id));
  const selected=sources.filter(s=>selectedIds.includes(s.id));
  const wordCount=selected.reduce((count,s)=>count+s.words.length,0);
  const draftDuration=draft?storyDraftDuration(draft.passages,draftSources):0;
  const minutes=Number(targetMinutes),secondsPart=Number(targetRemainderSeconds);
  const targetSeconds=minutes*60+secondsPart;
  const validTarget=targetMinutes.trim()!=='' && targetRemainderSeconds.trim()!==''
    && Number.isInteger(minutes)&&Number.isInteger(secondsPart)
    && minutes>=0&&minutes<=15&&secondsPart>=0&&secondsPart<=59
    && targetSeconds>=30&&targetSeconds<=900;
  // The note below reflects the Story panel setting, not the editable AI draft target.
  useEffect(()=>{
    if(!open)return;
    const initial=initialDraftTargetSeconds(paper.targetRuntimeSeconds);
    setTargetMinutes(String(Math.floor(initial/60)));
    setTargetRemainderSeconds(String(initial%60));
  },[open,paper.targetRuntimeSeconds]);
  useEffect(()=>{setDraft(null);setChosenIds(null);setError('');setConfirmReplace(false);},[rootId]);
  useEffect(()=>()=>active.current?.abort(),[]);
  useEffect(()=>{
    if(open)return;
    active.current?.abort();active.current=null;
  },[open]);
  useEffect(()=>{
    if(!open)return;
    const controller=new AbortController();
    getAccessToken().then(token=>{
      if(!token)throw new Error('Sign in to generate a story.');
      return fetch(resolveApiBase()+'/api/meaning-search/status',{headers:{Authorization:'Bearer '+token},signal:controller.signal});
    }).then(response=>response.json().then(json=>{if(!response.ok)throw new Error(json.error||'AI service unavailable.');return json;}))
      .then(json=>{if(!controller.signal.aborted)setEnabled(json.enabled===true);})
      .catch(()=>{if(!controller.signal.aborted)setEnabled(false);});
    return()=>controller.abort();
  },[open,getAccessToken]);

  const toggleSource=id=>{
    setChosenIds(previous=>{
      const values=previous??allIds;
      return values.includes(id)?values.filter(value=>value!==id):[...values,id];
    });setDraft(null);setError('');
  };
  const generate=async event=>{
    event.preventDefault();if(busy)return;
    if(selected.length<1){setError('Select at least one transcribed interview.');return;}
    if(selected.length>8||wordCount>16000){setError('Use up to 8 interviews and 16,000 words. Deselect some interviews.');return;}
    if(brief.trim().length<10){setError('Describe the story in at least 10 characters.');return;}
    const seconds=targetSeconds;
    if(!validTarget){setError('Choose a target between 30 seconds and 15 minutes.');return;}
    const controller=new AbortController();active.current?.abort();active.current=controller;
    setBusy(true);setError('');setDraft(null);setConfirmReplace(false);
    try{
      const token=await getAccessToken();
      if(!token)throw new Error('Sign in before generating a story.');
      const response=await fetch(resolveApiBase()+'/api/story-draft',{
        method:'POST',headers:{'Content-Type':'application/json',Authorization:'Bearer '+token},
        signal:AbortSignal.any([controller.signal,AbortSignal.timeout(120000)]),
        body:JSON.stringify({brief:brief.trim(),videoType,targetSeconds:seconds,sources:storyRequestSources(selected,paper.editorialTags||[])})
      });
      const data=await response.json().catch(()=>({}));
      if(!response.ok)throw new Error(data.error||'Could not generate a story draft.');
      if(!Array.isArray(data.passages)||!data.passages.length)throw new Error('The story was empty. Try a different brief.');
      if(controller.signal.aborted)return;
      setDraft({...data,targetSeconds:seconds,passages:data.passages.map(p=>({...p,uid:crypto.randomUUID()}))});
      setDraftSources(selected);
      setApplyMode(paper.blocks?.length?'append':'replace');
      // Generated results are further down the same scrollable dialog.
      // Bring the editable story into view rather than leaving the user
      // looking at the Generate button wondering what happened.
      requestAnimationFrame(()=>document.querySelector('.ai-story-results')?.scrollIntoView({block:'start',behavior:'smooth'}));
    }catch(e){
      if(!controller.signal.aborted)setError(e.name==='TimeoutError'?'The AI took too long. Try fewer interviews or a shorter brief.':e.message);
    }finally{if(active.current===controller){active.current=null;setBusy(false);}}
  };
  const changeSectionTitle=(stage,title)=>{
    setDraft(current=>({...current,sections:(current.sections||[]).map(s=>
      s.stage===stage?{...s,title}:s)}));
    setConfirmReplace(false);
  };
  const changePassage=(uid,update)=>{setDraft(current=>({...current,passages:current.passages.map(p=>p.uid===uid?{...p,...update}:p)}));setConfirmReplace(false);};
  const move=(index,direction)=>{
    setDraft(current=>{
      const passages=[...current.passages],next=index+direction;
      if(next<0||next>=passages.length)return current;
      passages.splice(next,0,passages.splice(index,1)[0]);
      return {...current,passages};
    });setConfirmReplace(false);
  };
  const useDraft=async()=>{
    if(busy||!draft?.passages.length)return;
    if(paper.blocks?.length && applyMode==='replace'&&!confirmReplace){setConfirmReplace(true);return;}
    setBusy(true);setApplying(true);setError('');
    try{
      const next=buildPaperFromStory(paper,draft,draftSources,storySourcesFromRows(rows,rootId),{append:applyMode==='append'});
      await onUse(next);
      setDraft(null);setConfirmReplace(false);onClose();
    }catch(e){setError(e.message||'Could not apply this draft.');}
    finally{setApplying(false);setBusy(false);}
  };
  if(!open)return null;
  return <div className="modal-backdrop ai-story-backdrop" role="presentation" onMouseDown={event=>{if(event.target===event.currentTarget&&!busy)onClose();}}>
    <section className="ai-story-modal" role="dialog" aria-modal="true" aria-labelledby="ai-story-title" aria-describedby="ai-story-subtitle" onKeyDown={event=>{if(event.key==='Escape'&&!busy){event.stopPropagation();onClose();}}}>
      <header className="ai-story-modal-header ai-story-compact-header">
        <span className="ai-story-head-icon" aria-hidden="true"><FiLayers/></span>
        <div className="ai-story-hero-copy">
          <h2 id="ai-story-title">Complete Story</h2>
          <p id="ai-story-subtitle">Build a paper edit from your interviews with AI.</p>
        </div>
        <button type="button" className="ai-story-close" disabled={busy} onClick={onClose} aria-label="Close Complete Story" title="Close"><FiX aria-hidden="true"/></button>
      </header>
      <div className="ai-story-scroll">
        <form className="ai-story-form" onSubmit={generate}>
          <div className="ai-story-section-heading">
            <h3>Story brief</h3>
            <label className="ai-story-brief-import"><FiUploadCloud aria-hidden="true"/> Import brief
              <input type="file" accept=".pdf,.txt,.md,.markdown,.rtf,.docx,application/pdf,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                disabled={busy||briefImporting}
                onChange={async event=>{
                  const file=event.target.files?.[0];event.target.value='';
                  if(!file)return;
                  setBriefImporting(true);setBriefImportError('');
                  try{
                    const imported=await readProductionBrief(file);
                    setBrief(imported);setBriefFile(file.name);setDraft(null);setError('');
                  }catch(e){setBriefImportError(e.message||'Could not read that brief.');}
                  finally{setBriefImporting(false);}
                }}/>
            </label>
          </div>
          {briefFile&&<p className="ai-story-imported"><FiCheck aria-hidden="true"/> {briefFile} imported</p>}
          {briefImporting&&<p className="ai-story-imported">Reading brief…</p>}
          {briefImportError&&<p className="ai-story-error" role="alert">{briefImportError}</p>}
          <label className="ai-story-field">What should the story focus on?
            <textarea rows={3} maxLength={1800} value={brief} onChange={event=>{setBrief(event.target.value);setBriefFile('');setDraft(null);setError('');}}
              placeholder="The angle, main message and any key points to include…" disabled={busy} required/>
          </label>
          <div className="ai-story-config">
            <label className="ai-story-field">Video type
              <select value={videoType} disabled={busy} onChange={event=>{setVideoType(event.target.value);setDraft(null);setError('');}}>{TYPES.map(([id,label])=><option key={id} value={id}>{label}</option>)}</select>
            </label>
            <fieldset className="ai-story-duration">
              <legend>Target length</legend>
              <div className="ai-story-duration-inputs">
                <label>Minutes
                  <input type="number" min="0" max="15" step="1" inputMode="numeric" value={targetMinutes} disabled={busy}
                    onChange={event=>{setTargetMinutes(event.target.value);setDraft(null);setError('');}}/>
                </label>
                <span aria-hidden="true" className="ai-story-duration-separator">:</span>
                <label>Seconds
                  <input type="number" min="0" max="59" step="1" inputMode="numeric" value={targetRemainderSeconds} disabled={busy}
                    onChange={event=>{setTargetRemainderSeconds(event.target.value);setDraft(null);setError('');}}/>
                </label>
              </div>
              {paper.targetRuntimeSeconds&&<small className="ai-story-project-target-note">{projectTargetNote(paper.targetRuntimeSeconds)}</small>}
              {!validTarget&&<small role="alert">Enter an AI draft target between 30 seconds and 15 minutes.</small>}
            </fieldset>
          </div>
          <div className="ai-story-section-heading ai-story-source-intro">
            <h3>Interviews</h3>
            <span className="ai-story-source-count">{selected.length} selected · {wordCount.toLocaleString()} words</span>
          </div>
          <fieldset className="ai-story-sources" disabled={busy}>
            <legend className="sr-only">Interview sources</legend>
            {!sources.length?<p>Transcribe an interview to build a story.</p>:sources.map(source=><label key={source.id}>
              <input type="checkbox" checked={selectedIds.includes(source.id)} onChange={()=>toggleSource(source.id)}/>
              <span><strong>{source.title||source.filename||'Untitled interview'}</strong><small>{source.words.length.toLocaleString()} words</small></span>
            </label>)}
            {(selected.length>8||wordCount>16000)&&<small className="ai-story-limit">Choose up to 8 interviews and 16,000 words.</small>}
          </fieldset>
          <details className="ai-story-privacy"><summary><FiFileText aria-hidden="true"/> How interview text is used</summary>
            <p>Your brief and selected interview words are sent to Gemini to draft a story using real quotes. Nothing changes in Paper Edit until you apply the draft.</p>
          </details>
          <div className="ai-story-form-actions">
            <span className="ai-story-form-status" role="status">{enabled===false?'AI generation is unavailable.':busy?(applying?'Checking cut audio…':'Building your story draft…'):draft?'Review the draft below, or regenerate.':''}</span>
            <button type="button" className="ai-story-cancel" onClick={onClose} disabled={busy}>Cancel</button>
            <button type="submit" className="ai-story-primary" disabled={busy||enabled!==true||!selected.length||selected.length>8||wordCount>16000||brief.trim().length<10||!validTarget}>
              {busy?'Creating…':draft?'Regenerate draft':'Generate draft'} <FiArrowRight aria-hidden="true"/>
            </button>
          </div>
        </form>
        {error&&<p className="ai-story-error" role="alert">{error}</p>}
        {draft&&<section className="ai-story-results" aria-label="Editable story draft" aria-live="polite">
          <div className="ai-story-results-header"><div><h3>{draft.title||'Story draft'}</h3><p>{draft.summary}</p></div>
            <div className="ai-story-runtime"><strong>{formatTime(draftDuration)}</strong><small>Selected footage</small><small>Target {formatTime(draft.targetSeconds)}</small></div>
          </div>
          <p className="ai-story-warning">Review the quotes and their order before applying. AI-generated explanations may need checking.</p>
          {draft.unmetMustUse?.length>0&&<p className="ai-story-priority-warning" role="status">
            {draft.unmetMustUse.length} Must use selection{draft.unmetMustUse.length===1?' was':'s were'} not included by this draft. Review the suggested story before applying, or regenerate with a more focused brief.
          </p>}
          <div className="ai-story-passage-list">{draft.passages.map((p,i)=>{
            const source=draftSources.find(s=>s.id===p.sourceId);
            const words=source?.words.slice(p.start,p.end+1)||[];
            const duration=words.length?words.at(-1).end-words[0].start:0;
            const firstInStage=!draft.passages.slice(0,i).some(earlier=>earlier.section===p.section);
            const chapter=draft.sections?.find(section=>section.stage===p.section);
            return <article className="ai-story-passage" key={p.uid}>
              {firstInStage&&chapter&&<div className="ai-story-chapter-preview">
                <span>CHAPTER · {SECTIONS.find(([stage])=>stage===p.section)?.[1]||p.section}</span>
                <label>Suggested section heading
                  <input type="text" maxLength={80} value={chapter.title}
                    onChange={event=>changeSectionTitle(p.section,event.target.value)}
                    aria-label={'Chapter title for '+p.section}/>
                </label>
                {chapter.reason&&<details className="ai-story-passage-details"><summary>Why this section?</summary><p>{chapter.reason}</p></details>}
              </div>}
              <div className="ai-story-passage-heading"><div><strong>{String(i+1).padStart(2,'0')} · {source?.title||'Missing source'}</strong><span>{words.length?formatTime(words[0].start):'—'} source IN · {formatTime(duration)} long</span></div>
                <select aria-label={'Story section for passage '+(i+1)} value={p.section} onChange={event=>changePassage(p.uid,{section:event.target.value})}>{SECTIONS.map(([id,label])=><option key={id} value={id}>{label}</option>)}</select>
              </div>
              <blockquote>{words.map(w=>w.text).join(' ')}</blockquote>
              {(p.reason||p.context)&&<details className="ai-story-passage-details">
                <summary>Editorial notes</summary>
                {p.reason&&<p>{p.reason}</p>}{p.context&&<p><strong>Check context:</strong> {p.context}</p>}
              </details>}
              <div className="ai-story-passage-tools"><button type="button" disabled={i===0} onClick={()=>move(i,-1)} aria-label={'Move passage '+(i+1)+' up'}>↑ Up</button>
                <button type="button" disabled={i===draft.passages.length-1} onClick={()=>move(i,1)} aria-label={'Move passage '+(i+1)+' down'}>↓ Down</button>
                <button type="button" onClick={()=>{onClose();onLocate(p,source);}}>Show in transcript</button>
                <button type="button" className="ai-story-remove" onClick={()=>{setDraft(current=>({...current,passages:current.passages.filter(x=>x.uid!==p.uid)}));setConfirmReplace(false);}}>Remove</button></div>
            </article>;
          })}</div>
          <footer className="ai-story-apply">
            <div><strong>Add this draft to Paper Edit</strong>
              {paper.blocks?.length>0&&<div className="ai-story-apply-options"><label><input type="radio" checked={applyMode==='append'} onChange={()=>{setApplyMode('append');setConfirmReplace(false);}}/> Add after current edit ({paper.blocks.length} passages)</label>
                <label><input type="radio" checked={applyMode==='replace'} onChange={()=>{setApplyMode('replace');setConfirmReplace(false);}}/> Replace current edit</label></div>}
              <small>Your current edit is backed up in Versions.</small>
            </div>
            <button className="ai-story-primary" type="button" disabled={busy||!draft.passages.length} onClick={useDraft}>
              {applying?'Checking cut audio…':confirmReplace?'Confirm replacement':applyMode==='append'&&paper.blocks?.length?'Add to Assembly':'Use this draft'}
            </button>
          </footer>
        </section>}
      </div>
    </section>
  </div>;
}
